<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        max-width="500px"
    >
        <!-- Header -->
        <div slot="header">
            <div>{{ $t('client_transactions.transaction') }}</div>
        </div>

        <!-- Content -->
        <div slot="content" class="item-form">
            <v-row>
                <v-col class="pt-3 pb-0" cols="12">
                    <v-btn-toggle
                        v-model="incoming"
                        class="mb-2 wfull d-flex"
                        mandatory
                        dense
                    >
                        <v-btn
                            class="flex-1"
                            color="primary"
                            text
                            :value="false"
                        >
                            {{ $t('client_transactions.to_client') }}
                        </v-btn>
                        <v-btn
                            class="flex-1"
                            color="primary"
                            text
                            :value="true"
                        >
                            {{ $t('client_transactions.from_client') }}
                        </v-btn>
                    </v-btn-toggle>
                </v-col>
            </v-row>

            <v-row>
                <v-col class="pt-3 pb-0" cols="12">
                    <v-autocomplete
                        v-model="form.client_id"
                        :items="clients"
                        item-text="name"
                        item-value="id"
                        :disabled="!!clientId"
                        :label="$tc('general.client', 1)"
                        :error-messages="form.$errors.client_id"
                        menu-props="offsetY"
                        @blur="form.validate('client_id')"
                        @input="delete form.$errors.client_id"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col class="pt-3 pb-0" cols="12" sm="6">
                    <v-select
                        v-model="form.basis_display"
                        :error-messages="form.$errors.basis_display"
                        :label="$t('client_transactions.basis.heading')"
                        :items="bases"
                        item-value="value"
                        item-text="display"
                        :disabled="!!basis"
                        @blur="form.validate('basis_display')"
                        @input="delete form.$errors.basis_display"
                    />
                </v-col>

                <v-col class="pt-3 pb-0" cols="12" sm="6">
                    <v-text-field
                        v-model="form.document_number"
                        :error-messages="form.$errors.document_number"
                        :label="$t('client_transactions.document_number')"
                        :disabled="!!documentNumber"
                        @blur="form.validate('document_number')"
                        @input="delete form.$errors.document_number"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col class="pt-3 pb-0" cols="12">
                    <number-field
                        v-model="form.amount"
                        :label="$t('client_transactions.amount')"
                        :precision="2"
                        unsigned
                        use-string
                        :error-messages="form.$errors.amount"
                        @blur="form.validate('amount');"
                        @input="delete form.$errors.amount"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col class="pt-3 pb-0" cols="12">
                    <v-text-field
                        v-model="form.note"
                        :error-messages="form.$errors.note"
                        :label="$t('general.note')"
                        @blur="form.validate('note')"
                        @input="delete form.$errors.note"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col class="pt-3 pb-0" cols="12" sm="6">
                    <v-menu
                        v-model="picker1"
                        :close-on-content-click="false"
                        :nudge-right="32"
                        transition="scale-transition"
                        min-width="290"
                        offset-y
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="form.date"
                                :error-messages="form.$errors.date"
                                :label="$t('general.date')"
                                prepend-icon="fal fa-calendar"
                                readonly
                                v-on="on"
                                @blur="form.validate('date')"
                                @input="delete form.$errors.date"
                            />
                        </template>
                        <v-date-picker
                            v-model="form.date"
                            @input="picker1 = false; form.validate('date')"
                        />
                    </v-menu>
                </v-col>

                <v-col class="pt-3 pb-0" cols="12" sm="6">
                    <v-menu
                        v-model="picker2"
                        :close-on-content-click="false"
                        :nudge-right="32"
                        transition="scale-transition"
                        min-width="290"
                        offset-y
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="form.due_date"
                                :error-messages="form.$errors.due_date"
                                :label="$t('client_transactions.due_date')"
                                prepend-icon="fal fa-calendar"
                                readonly
                                v-on="on"
                                :disabled="form.basis_display == 'payment' || form.basis_display == 'write_off'"
                                @blur="form.validate('due_date')"
                                @input="delete form.$errors.due_date"
                            />
                        </template>
                        <v-date-picker
                            v-model="form.due_date"
                            :min="form.date"
                            @input="picker2 = false; form.validate('due_date')"
                        />
                    </v-menu>
                </v-col>
            </v-row>
        </div>

        <!-- Footer -->
        <template slot="footer">
            <v-btn
                color="primary"
                :disabled="form.$busy"
                :loading="form.$busy"
                class="flex-1"
                @click="save"
            >
                {{ $t('general.save') }}
            </v-btn>
        </template>

    </cod-dialog>
</template>

<script>
import NumberField from '@/components/NumberField';
import CodDialog from '@/components/CodDialog';
import ClientTransaction from '@/models/ClientTransaction';
import Client from '@/models/Client';
import ApiService from '@/services/ApiService';
import { required, minValue } from 'vuelidate/lib/validators';

export default {
    components: {
        NumberField,
        CodDialog,
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        item: {
            type: Object,
            default: () => null
        },
        clientId: {
            type: Number,
            default: null
        },
        bankStatementId: {
            type: Number,
            default: null
        },
        documentNumber: {
            type: String,
            default: null
        },
        date: {
            type: String,
            default: null
        },
        basis: {
            type: String,
            default: null
        },
        clients: {
            type: Array,
            default: () => Client.all()
        }
    },

    data() {
        return {
            showDialog: false,
            form: this.createForm(),
            picker1: null,
            picker2: null,

            incoming: false,
        };
    },

    validations() {
        return {
            form: {
                client_id: { required },
                document_number: { required },
                basis_display: { required },
                date: { required },
                amount: { required, minValue: minValue(0) },
            }
        }
    },

    watch: {
        show(val) {
            this.showDialog = val;

            if (val) {
                this.form = this.createForm(this.item);
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },

        'form.date'(val) {
            this.form.due_date = null;
        },

        'form.basis_display'(val) {
            if([ClientTransaction.BASIS_PAYMENT, ClientTransaction.BASIS_WRITE_OFF].includes(val)) {
                this.form.due_date = null;
            }
        },
    },

    computed: {
        bases() {
            const bases = [
                { value: 'payment', display: this.incoming ? this.$t('client_transactions.basis.client_payment') : this.$t('client_transactions.basis.payment') },
                { value: 'write_off', display: this.$t('client_transactions.basis.write_off') },
                { value: 'sale', display: this.$t('client_transactions.basis.sale') },
                { value: 'refund', display: this.$t('client_transactions.basis.refund') },
                { value: 'advance', display: this.$t('client_transactions.basis.advance') },
                { value: 'credit_note', display: this.$t('client_transactions.basis.credit_note') },
            ]

            if(locale === 'me') {
                return [
                    ...bases,
                    { value: 'order', display: this.$t('client_transactions.basis.order') },
                    // { value: 'summary', display: this.$t('client_transactions.basis.summary') },
                ];
            }

            return bases;
        }
    },

    methods: {
        createForm(item) {
            this.incoming = item ? item.basis.includes('client_') : false;

            const basisDisplay = item?.basis ? item.basis.replace('client_', '') : null;
            let amount = null;
            if(item) {
                amount = parseFloat(item.debt) > 0 ? item.debt : item.credit;
            }

            return new Form(this, 'form', {
                id: item?.id || null,
                client_id: item?.client_id || this.clientId,
                order_id: item?.order_id || null,
                bank_statement_id: item?.bank_statement_id || this.bankStatementId,
                document_number: item?.document_number || this.documentNumber,
                type: item?.type || 'manual',
                basis: item?.basis || null,
                date: item?.date
                    ? moment(item.date).format('YYYY-MM-DD')
                    : (this.date ? moment(this.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')),
                due_date: item?.due_date ? moment(item.due_date).format('YYYY-MM-DD') : null,
                debt: item?.debt || null,
                credit: item?.credit || null,
                note: item?.note || null,
                amount: amount,
                basis_display: basisDisplay || this.basis,
                confirmed: true,
            });
        },

        prepareForSave() {
            this.form.basis = this.incoming
                ? ('client_' + this.form.basis_display)
                : this.form.basis_display;

            switch(this.form.basis_display) {
                case ClientTransaction.BASIS_REFUND:
                case ClientTransaction.BASIS_CREDIT_NOTE:
                case ClientTransaction.BASIS_WRITE_OFF:
                    this.form.debt = !this.incoming ? '0.00' : this.form.amount;
                    this.form.credit = !this.incoming ? this.form.amount : '0.00';
                    break;
                case ClientTransaction.BASIS_SALE:
                case ClientTransaction.BASIS_ADVANCE:
                case ClientTransaction.BASIS_ORDER:
                case ClientTransaction.BASIS_PAYMENT:
                    this.form.debt = !this.incoming ? this.form.amount : '0.00';
                    this.form.credit = !this.incoming ? '0.00' : this.form.amount;
                    break;
                default:
                    break;
            }
        },

        async save() {
            if(!this.form.validate()) return;

            this.prepareForSave();

            const method = this.form.id ? 'patch' : 'post';
            const url = '/admin/client-transactions' + (this.form.id ? `/${this.form.id}` : '');

            const response = await ApiService[method](url, this.form);
            this.$emit('save', new ClientTransaction(response.data));
            this.$snackbar(this.$t('client_transactions.saved'), 'success');

            this.form = this.createForm();
            this.showDialog = false;
        },
    }
};
</script>

<style lang="scss" scoped>
.item-form {
    padding: 24px;
    min-height: 280px;
}
</style>
